package top.wcpe.allincore.dsl.compiler.semantic

import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.StmtAst
import top.wcpe.allincore.dsl.compiler.ast.TypeAst
import top.wcpe.allincore.dsl.compiler.ast.displayName
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError
import top.wcpe.allincore.dsl.compiler.type.IrType
import top.wcpe.allincore.dsl.compiler.type.TypeInferencer
import top.wcpe.allincore.dsl.compiler.type.TypeResolver

/**
 * M10 Stage 1：拆分自原 TypeConstraintChecker。
 *
 * 维度：控制流类型一致性相关诊断（when 分支 / Elvis / is 运算符）。
 * 涉及诊断码：E665 / E667 / E668。
 *
 * 对应原文件方法：
 * - checkWhenBranchTypeConsistency
 * - inferBranchTypeOfStmts
 * - checkElvisTypeCompatibility
 * - checkIsTargetTypeKnown
 * - IS_TARGET_BUILTIN_CONTAINERS
 */
internal class ControlFlowTypeChecker(
    private val diag: DiagnosticCollector,
    private val env: VarTypeEnv,
) {

    /**
     * M9.5 Stage 4 / E665：when 表达式分支返回类型两两不容时报 INFO。
     *
     * 命中规则：
     * - 至少两个有可推断类型的分支（含 elseBody）
     * - 任意两个非 AnyT 分支类型 `a` / `b`：双向 isAssignableTo 都失败时报第一对不容组合
     * - AnyT 分支一律跳过，避免对绝大多数动态值产生假阳性
     *
     * 仅在第一对不容分支上报 1 次 INFO（避免 N×N 噪声）；上报位置选用 when 表达式自身
     * 的 position，让用户在 IDE gutter 上能直观定位到 `when` 关键字。
     */
    fun onWhenExpr(expr: ExprAst.WhenExpr) {
        val varEnv = env.currentVarEnv()
        val branchTypes = mutableListOf<IrType>()
        for (br in expr.branches) {
            val t = inferBranchTypeOfStmts(br.body, varEnv)
            if (t != IrType.AnyT) branchTypes += t
        }
        expr.elseBody?.let { stmts ->
            val t = inferBranchTypeOfStmts(stmts, varEnv)
            if (t != IrType.AnyT) branchTypes += t
        }
        if (branchTypes.size < 2) return
        // 找首对双向不容的组合
        for (i in branchTypes.indices) {
            for (j in i + 1 until branchTypes.size) {
                val a = branchTypes[i]
                val b = branchTypes[j]
                if (a == b) continue
                if (a.isAssignableTo(b) || b.isAssignableTo(a)) continue
                diag.info(
                    DslError.E665_WHEN_BRANCH_TYPE_INCONSISTENT,
                    expr.position,
                    a.displayName(),
                    b.displayName(),
                )
                return
            }
        }
    }

    /**
     * 推断一组 [stmts] 的分支返回类型——仅看最后一条 ExprStmt / ReturnStmt 的类型；
     * 其它形态（含空 body）一律返回 AnyT，让上层判定时跳过。
     *
     * 与 [TypeInferencer.inferStmtListType] 等价但 internal 不可访问；这里就地实现一份
     * 简化版（只处理 when 分支末端常见形态）。
     */
    private fun inferBranchTypeOfStmts(stmts: List<StmtAst>, varEnv: Map<String, IrType>): IrType {
        if (stmts.isEmpty()) return IrType.AnyT
        return when (val last = stmts.last()) {
            is StmtAst.ExprStmt -> TypeInferencer.inferExpr(last.call, varEnv)
            is StmtAst.ReturnStmt -> last.value?.let { TypeInferencer.inferExpr(it, varEnv) } ?: IrType.UnitT
            else -> IrType.AnyT
        }
    }

    /**
     * M9.5 Stage 4 / E667：Elvis `a ?: b` 的左侧 inner 与右侧类型不容时报 INFO。
     *
     * 命中规则：
     * - 左侧推断类型 `Lt`，剥掉外层 Nullable 后得到 inner `Li`
     * - 右侧推断类型 `Rt`
     * - `Li` 与 `Rt` 都不是 AnyT，且双向 isAssignableTo 都失败 → 报 INFO
     *
     * 例：`val a: String? = ...; a ?: 0`——Li=StringT，Rt=NumberT，不容；
     * 而 `a ?: ""` 与 `a ?: null` 都 OK。
     */
    fun onElvis(expr: ExprAst.Elvis) {
        val varEnv = env.currentVarEnv()
        val leftType = TypeInferencer.inferExpr(expr.left, varEnv)
        val rightType = TypeInferencer.inferExpr(expr.right, varEnv)
        val leftInner = if (leftType is IrType.Nullable) leftType.inner else leftType
        if (leftInner == IrType.AnyT) return
        // 右侧为 AnyT 或 Nullable(AnyT)（如 null 字面量被推断为 Nullable(AnyT)）一律放行
        if (rightType.isDynamic()) return
        if (leftInner.isAssignableTo(rightType) || rightType.isAssignableTo(leftInner)) return
        diag.info(
            DslError.E667_ELVIS_RIGHT_TYPE_INCOMPATIBLE,
            expr.position,
            rightType.displayName(),
            leftInner.displayName(),
        )
    }

    /**
     * M9.5 Stage 4 / E668：is 运算符目标类型未识别时报 INFO。
     *
     * 命中规则：[ExprAst.IsExpr.typeName] 既不能被 [TypeResolver] 识别为内置命名类型 / 容器，
     * 也不在当前 screen 的 dataTypeNames 中。允许带可空标记 `?` 的写法（剥掉再查表）。
     *
     * `is` 运算符在运行时支持 `Number/Int/Long/Double/String/Boolean/Bool/Map/List/Any/Unit`
     * （详见 ExprEvaluator.checkIsType）；TypeResolver 把 List/Map 放在 GENERIC 表（带泛型参数），
     * 这里需要单独把它们加入"裸名识别白名单"，否则 `x is Map`/`x is List` 会被误报 E668。
     *
     * 第一版仅做"类型名能否解析"的存在性检查；真正的运行时 instanceof 行为由编译器与运行时
     * 共同保证。
     */
    fun onIsExpr(expr: ExprAst.IsExpr) {
        val rawName = expr.typeName
        val baseName = rawName.removeSuffix("?").trim()
        if (baseName.isEmpty()) return
        // 已注册 data 类型直接放行
        if (baseName in env.dataTypeNames) return
        // 内置容器类型名（裸名形态，is List / is Map）单独识别——TypeResolver 把它们放在 GENERIC 表，
        // 需要泛型参数才匹配；为支持运行时 `x is List` 直接放行
        if (baseName in IS_TARGET_BUILTIN_CONTAINERS) return
        // 其它命名类型走 TypeResolver 反查
        val asAst = TypeAst.Named(baseName, nullable = false, position = expr.position)
        if (TypeResolver.resolve(asAst) != null) return
        diag.info(
            DslError.E668_IS_TARGET_TYPE_UNKNOWN,
            expr.position,
            rawName,
        )
    }

    private companion object {
        /**
         * `is` 运算符在裸名形态下也支持的内置容器类型集合。
         *
         * 与 `ExprEvaluator.checkIsType` 保持一致——运行时把 `x is List` / `x is Map` 直接当
         * `instanceof java.util.List|Map` 处理，无须泛型参数。但 [TypeResolver] 把 List / Map
         * 列入 GENERIC 表（NAMED 表里没有），导致 `TypeResolver.resolve(Named("List"))` 返回 null，
         * 故在 E668 校验中把这类裸容器名单独识别出来放行。
         */
        private val IS_TARGET_BUILTIN_CONTAINERS: Set<String> = setOf("List", "Map")
    }
}
